import React from 'react';
import styled from 'styled-components';
import { BackButton } from 'autogen/translation-keys/trans-website-booking-shared';
import { OthersRead, MoreContent, ReadAlso } from 'autogen/translation-keys/trans-website-articleblock';
import type { InspirationArticleLeftHandBlockDTO } from 'autogen/swagger/all';
import { useTranslation } from 'react-i18next';
import { SvgSprite } from 'modules/svgSprite';
import { breakpoint, sizes } from 'variables';
import {
    FontFontFamilySecondary,
    FontFontFamilyPrimary,
    FontSize10,
    FontSize14,
    FontSize16,
    FontWeightSemiBold,
    FontLineHeightTight,
    Spacing16
} from 'autogen/design-tokens/tokens';
import PictureDeprecated from 'components/generic/picture/PictureDeprecated';

export type InspirationArticleLeftHandPlaceholderProps = {
    model: InspirationArticleLeftHandBlockDTO;
};

const Container = styled.div`
    display: flex;
    width: 100%;

    ${breakpoint.up(sizes.md)} {
        display: none;
    }
`;

const ArticleBlocksContainer = styled.div<{ open: boolean }>`
    display: ${(props) => (props.open ? 'block' : 'none')};
    padding: 20px 20px;
    border-top: solid 5px #666666;
    background: #ffffff;
    margin-top: 15px;

    ${breakpoint.up(sizes.md)} {
        display: block;
        margin-top: 0;
    }
`;

const ArticleBlockSection = styled.div`
    font-size: 1em;
    margin-bottom: 1em;
`;

const ArticleBlockTitle = styled.p`
    color: #666666;
    font-size: 1.23em;
    margin-bottom: 1em;
    line-height: 1.4em;
    font-family: ${FontFontFamilySecondary};
`;

const TagsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
`;

const Tag = styled.a`
    border: 1px solid #9f8f87;
    color: #9f8f87;
    padding: 0.6em 1em;
    margin: 0 0.8em 0.8em 0;
    font-family: ${FontFontFamilyPrimary};
    font-weight: bold;
    text-transform: uppercase;
    text-decoration: none;
`;

const StyledBackButton = styled.button`
    display: flex;
    flex: 1 1 0;
    align-items: center;
    margin: 1em 0 0.9285714286em;
    background: none;
    border: 0;
    cursor: pointer;
    font-weight: 700;

    & svg {
        width: 15px;
        height: 15px;
        margin-right: 5px;
    }
`;

const SubMenuButton = styled.button`
    display: none;
    flex: 1 1 0;
    justify-content: space-between;
    background-color: #ffffff;
    border: 1px solid #b8b8b8;
    border-top: 4px solid #002643;
    border-radius: 8px;
    padding: 1em 1.33em 0.9285714286em;
    font-weight: bold;
    &:hover {
        color: #002643;
    }
`;

const MoreContentContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
`;

const ArticleContainer = styled.div`
    display: flex;
    flex-direction: row;
    margin-bottom: 24px;
    gap: 0;

    ${breakpoint.up(sizes.md)} {
        gap: ${Spacing16};
        flex-direction: column;
    }

    ${breakpoint.up(sizes.xxl)} {
        gap: 0;
        flex-direction: row;
    }
`;

const ArticlePicture = styled.div`
    flex: 0 0 7.1428571429rem;
    width: 7.1428571429rem;
    margin-right: 12px;

    ${breakpoint.up(sizes.lg)} {
        flex: 0 0 10rem;
        width: 10rem;
        margin-right: 24px;
    }
`;

const ArticleContent = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    justify-content: center;
`;

const ArticleSectionName = styled.p`
    color: #000;
    font-family: ${FontFontFamilyPrimary};
    font-size: ${FontSize10};
    font-weight: ${FontWeightSemiBold};
    line-height: ${FontLineHeightTight};
    margin-bottom: 4px;
    text-transform: uppercase;
`;

const ArticleTitleUrl = styled.a`
    display: -webkit-box;
    -webkit-box-orient: vertical;
    color: #000;
    overflow: hidden;
    word-break: break-word;
    -webkit-line-clamp: 3;
    font-family: ${FontFontFamilySecondary};
    font-size: ${FontSize16};
    line-height: ${FontLineHeightTight};
    max-height: 3.7em;
    max-width: 100%;
`;

const ArticleReadMore = styled.a`
    margin-top: 12px;
    color: #000;
    font-size: ${FontSize14};
    font-weight: ${FontWeightSemiBold};
    line-height: ${FontLineHeightTight};
    text-decoration: underline;
`;

export default function InspirationArticleLeftHandPlaceholder(props: InspirationArticleLeftHandPlaceholderProps) {
    const { model } = props;
    const [open, setOpen] = React.useState<boolean>(false);
    const { t } = useTranslation();

    return (
        <div>
            <Container>
                {!model.hideNavigateBack && (
                    <StyledBackButton type="button">
                        <SvgSprite sprite="ArrowLeft" />
                        {t(BackButton)}
                    </StyledBackButton>
                )}
                <SubMenuButton onClick={() => setOpen(!open)}>
                    {t(OthersRead)}
                    <SvgSprite sprite="Dots" />
                </SubMenuButton>
            </Container>
            <ArticleBlocksContainer open={open}>
                <ArticleBlockSection>
                    <ArticleBlockTitle>{t(MoreContent)}</ArticleBlockTitle>
                    <TagsContainer>
                        {model.tags.map((tag) => (
                            <Tag key={tag.id} href={tag.url}>
                                {tag.name}
                            </Tag>
                        ))}
                    </TagsContainer>
                </ArticleBlockSection>
                <ArticleBlockSection>
                    <ArticleBlockTitle>{t(OthersRead)}</ArticleBlockTitle>
                    <MoreContentContainer>
                        {model.articles.map((article) => (
                            <ArticleContainer key={article.id}>
                                <ArticlePicture>
                                    <a href={article.url}>
                                        <PictureDeprecated {...article.image_1x1} />
                                    </a>
                                </ArticlePicture>
                                <ArticleContent>
                                    <ArticleSectionName>{article.section.name}</ArticleSectionName>
                                    <ArticleTitleUrl href={article.url}>{article.title}</ArticleTitleUrl>
                                    <ArticleReadMore href={article.url}>{t(ReadAlso)}</ArticleReadMore>
                                </ArticleContent>
                            </ArticleContainer>
                        ))}
                    </MoreContentContainer>
                </ArticleBlockSection>
            </ArticleBlocksContainer>
        </div>
    );
}
